import { legacyState } from '~/legacy/screens'
import { apiCreate } from '~/composables/useApi'

/**
 * Documents et exports : rien n'est généré dans le navigateur. Les PDF, les fichiers Excel, les lettres, les reçus et leurs codes-barres
 * sont produits par le backend Java (voir DocumentController) ; l'application ne fait que les demander, les afficher et les télécharger.
 */

/** Ajoute les paramètres renseignés à une adresse (les valeurs vides sont omises). */
function withParams(path: string, params: Record<string, unknown>): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') query.set(key, String(value))
  }
  const qs = query.toString()
  return qs ? `${path}?${qs}` : path
}

/** Récupère un fichier du backend et le télécharge sous le nom que le backend lui donne. */
async function saveFile(url: string, init?: RequestInit): Promise<void> {
  try {
    const response = await fetch(url, init)
    if (!response.ok) throw new Error(String(response.status))
    const blob = await response.blob()
    const disposition = response.headers.get('content-disposition') ?? ''
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'document'
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
    showToast(name.endsWith('.xlsx') ? 'Export Excel téléchargé.' : 'PDF téléchargé.')
  }
  catch (e) {
    console.error('[Registre] saveFile:', e)
    showToast('Le document n\'a pas pu être généré par le serveur.')
  }
}

/**
 * Ouvre un fichier du backend : un PDF dans un nouvel onglet (où il s'imprime ou s'enregistre), un fichier Excel ou un PDF marqué
 * `download=true` en téléchargement direct. C'est ce que déclenchent les boutons des documents.
 */
export function openBackendFile(url: string): void {
  const target = new URL(url, window.location.origin)
  const download = target.searchParams.get('download') === 'true'
  target.searchParams.delete('download')
  if (target.pathname.endsWith('/pdf') && !download) {
    target.searchParams.set('inline', 'true')
    window.open(target.toString(), '_blank')
    return
  }
  if (target.pathname.endsWith('/xlsx') && !target.searchParams.has('ecoleId')) {
    const ecoleId = useCurrentEcoleId().value
    if (ecoleId !== null) target.searchParams.set('ecoleId', String(ecoleId))
  }
  void saveFile(target.toString())
}

/** Les boutons PDF des états de la Caisse : le backend refait la feuille avec les mêmes filtres que l'écran. */
export function downloadPdf(name: string): void {
  const ecoleId = useCurrentEcoleId().value
  const s = legacyState
  if (name === 'suivi-des-soldes') {
    openBackendFile(withParams('/api/documents/soldes/pdf', {
      ecoleId, classeId: s.soldesFilterClasse, dateDebut: s.soldesFilterDateDebut, dateFin: s.soldesFilterDateFin,
    }))
  }
  else if (name === 'etats') {
    openBackendFile(withParams('/api/documents/etats/pdf', {
      classeId: s.caisseEtatsClasseId, anneeId: s.caisseEtatsAnneeId,
      dateDebut: s.caisseEtatsDateDebut, dateFin: s.caisseEtatsDateFin,
    }))
  }
  else if (name === 'echeances-datees') {
    openBackendFile(withParams('/api/documents/echeances/pdf', { ecoleId, date: s.echeancesDateesFilter }))
  }
  else if (name === 'relances') {
    openBackendFile(withParams('/api/documents/relances/pdf', {
      ecoleId, mois: s.relancesFilterDate, cycleId: s.relancesFilterCycleId, niveauId: s.relancesFilterNiveauId,
      classeId: s.relancesFilterClasseId, etudiantId: s.relancesFilterEtudiantId,
      dateDebut: s.relancesFilterDateDebut, dateFin: s.relancesFilterDateFin,
    }))
  }
  else if (name === 'relances-groupees') {
    openBackendFile(withParams('/api/documents/relances-groupees/pdf', { ids: (s.relanceBulkIds as string[]).join(',') }))
  }
  else {
    showToast('Rien à exporter en PDF.')
  }
}

export function exportSoldesCSV(): void {
  openBackendFile(withParams('/api/exports/soldes.xlsx', { ecoleId: useCurrentEcoleId().value, classeId: legacyState.soldesFilterClasse }))
}

export function exportCaisseEtatsExcel(): void {
  const { caisseEtatsClasseId, caisseEtatsAnneeId } = legacyState
  if (!caisseEtatsClasseId || !caisseEtatsAnneeId) {
    showToast('Sélectionne une classe et une année.')
    return
  }
  openBackendFile(withParams('/api/exports/etats.xlsx', { classeId: caisseEtatsClasseId, anneeId: caisseEtatsAnneeId }))
}

export function exportListeClasseExcel(classeId: string): void {
  openBackendFile(`/api/documents/liste-classe/${classeId}/xlsx`)
}

/** Export Excel d'une liste : le backend reçoit les lignes affichées (leurs identifiants) et écrit le fichier. */
export function exportEntityXlsx(key: string, ids: string[]): void {
  void saveFile(`/api/exports/entities/${key}.xlsx`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
}

/** Le reçu d'un versement : celui qui existe, sinon il est créé (comme `openOrPrintRecu` de la maquette). Le numéro est attribué par le backend à la création. */
async function ensureRecu(versementId: string): Promise<string | null> {
  const existing = useDb().value.recus?.find(r => r.versementId === versementId)
  if (existing) return existing.id as string
  const versement = useDb().value.versements?.find(v => v.id === versementId)
  if (!versement) {
    showToast('Versement introuvable.')
    return null
  }
  try {
    const saved = await apiCreate('recus', { versementId, montant: versement.montant, dateEmission: new Date().toISOString().slice(0, 10), annule: false })
    return saved.id as string
  }
  catch (e) {
    console.error('[Registre] ensureRecu:', e)
    showToast('Le reçu n\'a pas pu être créé.')
    return null
  }
}

export async function openOrPrintRecu(versementId: string): Promise<void> {
  const id = await ensureRecu(versementId)
  if (id !== null) goTo('recu-print', 'recus', id)
}

export async function printRecu(versementId: string): Promise<void> {
  const id = await ensureRecu(versementId)
  if (id !== null) openBackendFile(`/api/documents/recu/${id}/pdf`)
}

/** Contexte du reçu d'inscription (inscription et versements concernés) : renseigné par le parcours d'inscription. */
export function useRecuInscriptionContext() {
  return useState<{ inscriptionId: string, versementIds: string[] } | null>('recu-inscription-context', () => null)
}
